import { MAP_DEFINITIONS } from "../maps/index.js";
import { MONSTER_CONTENT, type MonsterContentId } from "../monsters/index.js";
import type { RegionUnlockRequirement } from "@ninja/domain";
import { RAW_REGION_BOSS_PROGRESSION } from "./registry.generated.js";

export interface RegionBossProgressionDefinition {
  schemaVersion: 1;
  regionId: string;
  regionName: string;
  unlockRequirement: RegionUnlockRequirement;
  marksLabel: string;
  sealLabel: string;
  sealsLabel: string;
  eliteMonsterId: MonsterContentId;
  bossMonsterId: MonsterContentId;
  marksPerSeal: number;
  maxPreparedSeals: number;
  bossCooldownMs: number;
}

const definitions = Object.values(
  RAW_REGION_BOSS_PROGRESSION,
) as RegionBossProgressionDefinition[];
const registry: Record<string, RegionBossProgressionDefinition> = {};

for (const definition of definitions) {
  if (
    definition.schemaVersion !== 1 ||
    !definition.regionId ||
    !definition.regionName ||
    !isRegionUnlockRequirement(definition.unlockRequirement) ||
    !definition.marksLabel ||
    !definition.sealLabel ||
    !definition.sealsLabel ||
    !Number.isInteger(definition.marksPerSeal) ||
    definition.marksPerSeal < 1 ||
    !Number.isInteger(definition.maxPreparedSeals) ||
    definition.maxPreparedSeals < 1 ||
    !Number.isSafeInteger(definition.bossCooldownMs) ||
    definition.bossCooldownMs < 0 ||
    registry[definition.regionId]
  )
    throw new TypeError("Region boss progression definition is invalid");

  const elite = MONSTER_CONTENT[definition.eliteMonsterId];
  const boss = MONSTER_CONTENT[definition.bossMonsterId];
  if (elite?.category !== "elite" || boss?.category !== "boss")
    throw new TypeError(
      `${definition.regionId} must reference one elite and one boss monster`,
    );

  const regionMaps = Object.values(MAP_DEFINITIONS).filter(
    (map) => map.regionNavigation?.regionId === definition.regionId,
  );
  if (
    !regionMaps.some((map) => map.regionNavigation?.role === "field") ||
    !regionMaps.some((map) => map.regionNavigation?.role === "boss_arena")
  )
    throw new TypeError(
      `${definition.regionId} must have field and boss-arena maps`,
    );

  registry[definition.regionId] = Object.freeze(definition);
}

for (const definition of Object.values(registry)) {
  const requirement = definition.unlockRequirement;
  if (requirement.kind !== "boss_victory") continue;
  const predecessor = registry[requirement.regionId];
  if (!predecessor || predecessor.bossMonsterId !== requirement.bossMonsterId)
    throw new TypeError(
      `${definition.regionId} references an unconfigured regional Boss prerequisite`,
    );
}

for (const map of Object.values(MAP_DEFINITIONS)) {
  const regionId = map.regionNavigation?.regionId;
  if (regionId && !registry[regionId])
    throw new TypeError(
      `${map.id} references region ${regionId} without a progression definition`,
    );
}

for (const regionId of Object.keys(registry)) {
  const visited = new Set<string>();
  let current: string | undefined = regionId;
  while (current) {
    if (visited.has(current))
      throw new TypeError(
        `Region unlock requirements contain a cycle at ${current}`,
      );
    visited.add(current);
    const requirement:
      RegionBossProgressionDefinition["unlockRequirement"] | undefined =
      registry[current]?.unlockRequirement;
    current =
      requirement?.kind === "boss_victory" ? requirement.regionId : undefined;
  }
}

export const REGION_BOSS_PROGRESSION: Readonly<
  Record<string, RegionBossProgressionDefinition>
> = Object.freeze(registry);

export function regionBossProgressionFor(
  regionId: string | undefined,
): RegionBossProgressionDefinition | undefined {
  return regionId ? REGION_BOSS_PROGRESSION[regionId] : undefined;
}

function isRegionUnlockRequirement(
  value: unknown,
): value is RegionUnlockRequirement {
  if (!value || typeof value !== "object") return false;
  const requirement = value as Partial<RegionUnlockRequirement>;
  return (
    requirement.kind === "none" ||
    (requirement.kind === "boss_victory" &&
      typeof requirement.regionId === "string" &&
      requirement.regionId.length > 0 &&
      typeof requirement.bossMonsterId === "string" &&
      requirement.bossMonsterId.length > 0)
  );
}
